import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import BackOfficeLayout from '../../components/backoffice/BackOfficeLayout';
import { useAuth } from '../../context/AuthContext';
import { useEnvironment } from '../../context/EnvironmentContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Bell, RefreshCw, CheckCircle, Info, AlertTriangle, Shield,
  FileCheck, MessageSquare, Clock
} from 'lucide-react';
import { toast } from '../../lib/safeToast';

const TYPE_CONFIG = {
  kyc:          { badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: FileCheck,     label: 'KYC' },
  security:     { badge: 'bg-blue-50 text-blue-700 border-blue-200',         icon: Shield,        label: 'Security' },
  system:       { badge: 'bg-slate-50 text-slate-600 border-slate-200',      icon: Info,          label: 'System' },
  payment:      { badge: 'bg-purple-50 text-purple-700 border-purple-200',   icon: CheckCircle,   label: 'Payment' },
  alert:        { badge: 'bg-amber-50 text-amber-700 border-amber-200',      icon: AlertTriangle, label: 'Alert' },
  compliance:   { badge: 'bg-teal-50 text-teal-700 border-teal-200',         icon: FileCheck,     label: 'Compliance' },
};

function getTypeConfig(type) {
  return TYPE_CONFIG[type] || TYPE_CONFIG.system;
}

function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleString('en-AU', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return '—'; }
}

export default function BackOfficeNotificationsLog() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { isLive } = useEnvironment();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('__all__');

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/notifications');
      let all = res.data?.notifications || res.data?.data || [];
      if (filter !== '__all__') {
        all = all.filter(n => n.type === filter);
      }
      setNotifications(all);
    } catch {
      toast.error('Failed to load notifications');
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [api, filter]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications, isLive]);

  const markAllRead = async () => {
    try {
      await api.post('/notifications/mark-all-read');
      toast.success('All notifications marked as read');
      fetchNotifications();
    } catch { toast.error('Failed to mark notifications as read'); }
  };

  return (
    <BackOfficeLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4 pb-4">
          <div>
            <h1 className="text-2xl font-bold text-[#073B2E] flex items-center gap-2">
              <Bell className="w-7 h-7 text-[#0B6445]" />
              Notification Log
            </h1>
            <p className="text-[#0E1B16]/70 mt-2">All notifications and platform events.</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Notifications</SelectItem>
                <SelectItem value="kyc">KYC</SelectItem>
                <SelectItem value="security">Security</SelectItem>
                <SelectItem value="system">System</SelectItem>
                <SelectItem value="compliance">Compliance</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={markAllRead}
              className="border-[#1C472B] text-[#1C472B] hover:bg-[#E6F7F3] hover:text-[#073B2E] text-xs"
            >
              Mark all read
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchNotifications}
              className="border-[#1C472B] text-[#1C472B] hover:bg-[#E6F7F3] hover:text-[#073B2E]"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <Card className="border-[#DCE4E0] shadow-sm">
          <CardContent className="pt-6">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="spinner w-8 h-8" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-12">
                <Bell className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-[#0E1B16]/60">No notifications found.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {notifications.map((n) => {
                  const tc = getTypeConfig(n.type);
                  const TypeIcon = tc.icon;
                  const actionPath = n.details?.action_path;
                  const merchantId = n.details?.merchant_id;
                  const href = actionPath || (merchantId ? `/backoffice/kyc/pending?merchant_id=${merchantId}` : null);
                  const isClickable = href && (n.type === 'kyc' || n.type === 'compliance');
                  return (
                    <div
                      key={n.id}
                      role={isClickable ? 'button' : undefined}
                      tabIndex={isClickable ? 0 : undefined}
                      onClick={isClickable ? () => navigate(href) : undefined}
                      onKeyDown={isClickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(href); } } : undefined}
                      className={`flex items-start gap-4 p-4 rounded-lg border transition-colors ${
                        n.read
                          ? 'border-[#DCE4E0]/60 bg-white'
                          : 'border-[#0B6445]/20 bg-[#F0FAF6]'
                      } ${isClickable ? 'cursor-pointer hover:bg-[#E6F7F3] hover:border-[#0B6445]/30' : ''}`}
                    >
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                        n.read ? 'bg-slate-100' : 'bg-[#E6F7F3]'
                      }`}>
                        <TypeIcon className={`w-4.5 h-4.5 ${n.read ? 'text-slate-400' : 'text-[#0B6445]'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className={`text-sm font-semibold ${n.read ? 'text-slate-600' : 'text-[#073B2E]'}`}>
                            {n.title}
                          </p>
                          {!n.read && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#0B6445] text-white">
                              New
                            </span>
                          )}
                          <Badge variant="outline" className={`${tc.badge} text-[10px] ml-auto`}>
                            {tc.label}
                          </Badge>
                        </div>
                        <p className={`text-sm leading-relaxed ${n.read ? 'text-slate-400' : 'text-slate-600'}`}>
                          {n.message}
                        </p>
                        <div className="flex items-center gap-2 mt-1.5">
                          <Clock className="w-3 h-3 text-slate-400" />
                          <span className="text-xs text-slate-400">
                            {n.created_at ? formatDateTime(n.created_at) : n.time || '—'}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </BackOfficeLayout>
  );
}
